import BlockBase from '@components/dnd/base/blockBase';
import { ColumnComp } from '@components/trace/column/column.comp';
import { EditorMode } from '@components/trace/column/editorMode.column';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB, {
  type SettingsConfig
} from '@library/formBuilder/endControls';
import { CommentEditor } from '@library/tableEditor/editors/comment.editor';
import { InputEditor } from '@library/tableEditor/editors/input.editor';
import {
  type DNDBlock,
  type EditorType,
  ResolvedVariableType
} from '@stratumn/studio-shared';
import { TextWidget } from '@widgets/text/text.widget';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialTextData } from './text.data';
import { TextSettingsModal } from './text.settings';
import { TextIcon } from './text.utils';

const TextTableEditorBlock = (block: DNDBlock) => {
  const actionId = useParams()[ParamsList.ActionId] as string;

  const atom = useAtom({
    type: 'Table_Editor_Text',
    sourceId: block.id,
    parentId: actionId,
    parentKind: 'table_editor',
    initialData: initialTextData,
    variableInfo: {
      resolvedType: ResolvedVariableType.Text
    }
  });

  if (!atom) {
    return null;
  }

  const getModalSettings = (): SettingsConfig => ({
    title: atom.data.title || 'Text Column Title',
    content: <TextSettingsModal atom={atom} />
  });

  const getEndControls = () => (
    <EndControlsFB
      dataItem={atom}
      dndBlock={block}
      hasRequired
      settings={getModalSettings()}
    />
  );

  const renderEditorOptions = () => {
    if (atom.data.editorOptions.selectedEditor === 'input') {
      return <InputEditor atom={atom} />;
    }
    if (atom.data.editorOptions.selectedEditor === 'comment') {
      return <CommentEditor atom={atom} />;
    }
    return null;
  };
  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={getEndControls()}
      icon={TextIcon}
      title={atom.data.title}
    >
      <ColumnComp
        atomId={atom.id}
        isDisplayedByDefault={atom.data.isDisplayedByDefault}
        isfixedColumn={atom.data.isfixedColumn}
        onDisplayByDefaultChange={v => (atom.data.isDisplayedByDefault = v)}
        onFixedColumnChange={v => (atom.data.isfixedColumn = v)}
        onTitleChange={v => (atom.data.title = v)}
        onWidthChange={v => (atom.data.width = v)}
        title={atom.data.title}
        width={atom.data.width}
      />
      <EditorMode
        atomId={atom.id}
        canSelectEditorType
        isEditMode={atom.data.isEditMode}
        onIsEditModeChange={value => (atom.data.isEditMode = value)}
        onSelectedEditorTypeChange={value =>
          (atom.data.editorOptions.selectedEditor = value)
        }
        possibleEditorTypes={
          Object.keys(atom.data.editorOptions.editors) as EditorType[]
        }
        selectedEditorType={atom.data.editorOptions.selectedEditor}
      />
      {atom.data.isEditMode ? (
        renderEditorOptions()
      ) : (
        <TextWidget
          atomId={atom.id}
          default={atom.data.cell.view.default}
          isRichText={atom.data.cell.view.isRichText}
          onDefaultChange={v => (atom.data.cell.view.default = v)}
          onIsRichTextChange={v => (atom.data.cell.view.isRichText = v)}
        />
      )}
    </BlockBase>
  );
};

export default observer(TextTableEditorBlock);
